import React, { createContext, useContext, ReactNode, useMemo, useCallback, useRef } from 'react';
import {
  useHomeScreenVisibilityComputed,
  useProfileScreenVisibilityComputed,
  useMyListScreenVisibilityComputed,
} from '@/hooks/useComputedScreenVisibility';

interface ScreenVisibilityContextType {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
}

const HomeVisibilityContext = createContext<ScreenVisibilityContextType | undefined>(undefined);
const ProfileVisibilityContext = createContext<ScreenVisibilityContextType | undefined>(undefined);
const MyListVisibilityContext = createContext<ScreenVisibilityContextType | undefined>(undefined);

const createDeprecatedSetter = (screenName: string): (visible: boolean) => void => {
  let hasWarnedRef = false;
  return (_visible: boolean) => {
    if (__DEV__ && !hasWarnedRef) {
      console.warn(
        `[DEPRECATED] setIsVisible for ${screenName} is deprecated. ` +
        `Visibility is now derived from navigation state and player context. ` +
        `This setter is a no-op and will be removed in a future version.`
      );
      hasWarnedRef = true;
    }
  };
};

export function HomeScreenVisibilityProvider({ children }: { children: ReactNode }) {
  const { isVisible: isHomeVisible } = useHomeScreenVisibilityComputed();
  const { isVisible: isProfileVisible } = useProfileScreenVisibilityComputed();
  const { isVisible: isMyListVisible } = useMyListScreenVisibilityComputed();

  const deprecatedHomeSetterRef = useRef(createDeprecatedSetter('Home'));
  const deprecatedProfileSetterRef = useRef(createDeprecatedSetter('Profile'));
  const deprecatedMyListSetterRef = useRef(createDeprecatedSetter('MyList'));

  const homeValue = useMemo(() => ({
    isVisible: isHomeVisible,
    setIsVisible: deprecatedHomeSetterRef.current,
  }), [isHomeVisible]);

  const profileValue = useMemo(() => ({
    isVisible: isProfileVisible,
    setIsVisible: deprecatedProfileSetterRef.current,
  }), [isProfileVisible]);

  const myListValue = useMemo(() => ({
    isVisible: isMyListVisible,
    setIsVisible: deprecatedMyListSetterRef.current,
  }), [isMyListVisible]);

  return (
    <HomeVisibilityContext.Provider value={homeValue}>
      <ProfileVisibilityContext.Provider value={profileValue}>
        <MyListVisibilityContext.Provider value={myListValue}>
          {children}
        </MyListVisibilityContext.Provider>
      </ProfileVisibilityContext.Provider>
    </HomeVisibilityContext.Provider>
  );
}

export function useHomeVisibility() {
  const context = useContext(HomeVisibilityContext);
  if (context === undefined) {
    throw new Error('useHomeVisibility must be used within a HomeScreenVisibilityProvider');
  }
  return context;
}

export function useProfileVisibility() {
  const context = useContext(ProfileVisibilityContext);
  if (context === undefined) {
    throw new Error('useProfileVisibility must be used within a HomeScreenVisibilityProvider');
  }
  return context;
}

export function useMyListVisibility() {
  const context = useContext(MyListVisibilityContext);
  if (context === undefined) {
    throw new Error('useMyListVisibility must be used within a HomeScreenVisibilityProvider');
  }
  return context;
}

interface LegacyHomeScreenVisibilityContextType {
  isHomeScreenVisible: boolean;
  setIsHomeScreenVisible: (visible: boolean) => void;
  isProfileScreenVisible: boolean;
  setIsProfileScreenVisible: (visible: boolean) => void;
  isMyListScreenVisible: boolean;
  setIsMyListScreenVisible: (visible: boolean) => void;
}

export function useHomeScreenVisibility(): LegacyHomeScreenVisibilityContextType {
  const home = useContext(HomeVisibilityContext);
  const profile = useContext(ProfileVisibilityContext);
  const myList = useContext(MyListVisibilityContext);

  if (home === undefined || profile === undefined || myList === undefined) {
    throw new Error('useHomeScreenVisibility must be used within a HomeScreenVisibilityProvider');
  }

  return useMemo(() => ({
    isHomeScreenVisible: home.isVisible,
    setIsHomeScreenVisible: home.setIsVisible,
    isProfileScreenVisible: profile.isVisible,
    setIsProfileScreenVisible: profile.setIsVisible,
    isMyListScreenVisible: myList.isVisible,
    setIsMyListScreenVisible: myList.setIsVisible,
  }), [home, profile, myList]);
}
